/* eslint-disable react/prop-types */
import { useQuery } from "@tanstack/react-query";
import useAxiosPublic from "../hooks/useAxiosPublic";
import LoadingPage from "../components/Layout/LoadingPage";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import image from "../assets/Academics/academics.webp";
import {
  Mail,
  Phone,
  GraduationCap,
  BookOpen,
  FileText,
  User,
} from "lucide-react";
import ImageHeading from "../components/ui/ImageHeading";

const FacultyDetails = () => {
  const axiosPublic = useAxiosPublic();
  const { id } = useParams();

  const {
    data: singleFacultyApiData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["singleFaculty", id],
    queryFn: async () => {
      const res = await axiosPublic.get(`/discover-zums/faculty/${id}`);
      return res.data;
    },
  });

  if (isLoading) return <LoadingPage />;

  if (error) {
    return (
      <div className="text-center py-20 text-primary-500">
        Error loading faculty details
      </div>
    );
  }

  const faculty = singleFacultyApiData;

  return (
    <div>
      <ImageHeading heading={faculty.name} imageUrl={image} />
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row items-center gap-8 mb-12"
        >
          <img
            src={faculty.image_url}
            alt={faculty.name}
            className="w-40 h-40 md:w-48 md:h-48 object-cover rounded-2xl shadow-lg"
          />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {faculty.name}
            </h1>
            <p className="text-lg text-gray-600 mb-4">{faculty.designation}</p>
            <p className="text-gray-700 italic">{faculty.bio}</p>
          </div>
        </motion.div>

        {/* Contact Info */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <InfoCard icon={Mail} title="Email" value={faculty.email} />
          <InfoCard icon={Phone} title="Phone" value={faculty.phone} />
        </div>

        {/* Expertise */}
        <SectionCard
          icon={GraduationCap}
          title="Areas of Expertise"
          points={faculty.expertise}
        />

        {/* Departments */}
        <SectionCard
          icon={User}
          title="Departments"
          points={faculty.department.map((d) => d.name)}
        />

        {/* Portfolio */}
        {faculty.portfolio && (
          <SectionCard
            icon={FileText}
            title="Curriculum Vitae"
            points={[`Download CV`]}
            links={[faculty.portfolio.cv_link]}
          />
        )}

        {/* Publications */}
        {faculty.publications_and_reports?.length > 0 && (
          <SectionCard
            icon={BookOpen}
            title="Publications & Reports"
            points={faculty.publications_and_reports.map(
              (p) => `${p.title} (${p.type})`
            )}
            links={faculty.publications_and_reports.map((p) => p.external_link)}
          />
        )}
      </div>
    </div>
  );
};

/* 🔹 Reusable InfoCard Component */
const InfoCard = ({ icon: Icon, title, value }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className="p-6 rounded-2xl bg-white/80 backdrop-blur-lg shadow-md border border-gray-100 flex items-center gap-4"
  >
    <div className="p-3 rounded-xl bg-primary-50">
      <Icon className="w-6 h-6 text-primary-600" />
    </div>
    <div>
      <p className="text-sm text-gray-500">{title}</p>
      <p className="text-gray-800 font-medium">{value}</p>
    </div>
  </motion.div>
);

/* 🔹 Reusable SectionCard */
const SectionCard = ({ icon: Icon, title, points, links }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    className="mb-10 p-8 bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-100"
  >
    <div className="flex items-center gap-4 mb-6">
      <div className="p-4 rounded-2xl bg-primary-50">
        <Icon className="w-7 h-7 text-primary-600" />
      </div>
      <h3 className="text-2xl font-bold text-gray-800">{title}</h3>
    </div>

    <ul className="space-y-3">
      {points.map((point, idx) => (
        <motion.li
          key={idx}
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: idx * 0.1 }}
          className="flex items-start gap-3"
        >
          <span className="w-2 h-2 mt-2 rounded-full bg-primary-500" />
          {links && links[idx] ? (
            <a
              href={links[idx]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-primary-600 transition"
            >
              {point}
            </a>
          ) : (
            <span className="text-gray-700">{point}</span>
          )}
        </motion.li>
      ))}
    </ul>
  </motion.div>
);

export default FacultyDetails;
